import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, TrendingUp, Search, Bot, FileText, Megaphone } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const auditIncludes = [
  {
    icon: Search,
    title: "AI Search Visibility Assessment",
    description: "See how visible you are in ChatGPT, Perplexity, Claude, and other AI search engines",
  },
  {
    icon: TrendingUp,
    title: "Competitor Analysis",
    description: "Understand how you stack up against competitors in traditional and AI search",
  },
  {
    icon: FileText,
    title: "Quick Wins Identification",
    description: "Get actionable recommendations you can implement immediately",
  },
  {
    icon: Bot,
    title: "AI Readiness Score",
    description: "Evaluate how prepared your marketing is for the AI-first future",
  },
];

const benefits = [
  "Current state of your AI search visibility",
  "Top 3 competitors' AI strategies",
  "Quick-win optimization opportunities",
  "Custom AI marketing roadmap",
  "Priority action items ranked by impact",
];

export default function AIGrowthAudit() {
  return (
    <Layout>
      <SEO 
        title="Free AI Growth Audit | TheCloudSEO"
        description="Get a free AI growth audit. Discover your visibility in AI search engines, identify quick wins, and get a custom roadmap for AI-powered growth."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container-wide relative z-10">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Resources", href: "/resources" },
              { label: "AI Growth Audit" },
            ]}
            className="mb-8"
          />
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <Badge className="mb-4 bg-green-500/10 text-green-500 hover:bg-green-500/20">
                Free Assessment
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                Free AI Growth <span className="text-gradient">Audit</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Discover how visible you are in AI-powered search, identify untapped opportunities, 
                and get a custom roadmap for AI-first marketing growth.
              </p>

              <ul className="space-y-3 mb-8">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>

              <Button 
                size="lg" 
                className="rounded-full"
                data-cal-link="thecloudseo/ai-growth-audit"
                data-cal-namespace="ai-growth-audit"
                data-cal-config='{"layout":"month_view"}'
              >
                Get Your Free Audit
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              <p className="text-sm text-muted-foreground mt-4">
                No commitment required. 30-minute session with an AI marketing expert.
              </p>
            </AnimatedSection>

            {/* What's Included */}
            <AnimatedSection delay={0.2}>
              <div className="bg-card rounded-3xl border border-border p-8">
                <h2 className="text-2xl font-bold mb-6">What's Included</h2>
                <div className="space-y-6">
                  {auditIncludes.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex gap-4"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <item.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 border-t border-border">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-muted-foreground">
              Simple 3-step process to get your personalized AI growth audit
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Book Your Session", description: "Schedule a 30-minute call at a time that works for you" },
              { step: "02", title: "We Analyze", description: "Our AI tools scan your presence across traditional and AI search" },
              { step: "03", title: "Get Your Roadmap", description: "Receive actionable recommendations and a custom growth plan" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative p-6 rounded-2xl bg-card border border-border text-center"
              >
                <div className="text-5xl font-bold text-primary/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Discover Your AI Growth Potential?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Get actionable insights in 30 minutes. No sales pitch, just value.
            </p>
            <Button 
              size="lg" 
              className="rounded-full"
              data-cal-link="thecloudseo/ai-growth-audit"
              data-cal-namespace="ai-growth-audit"
              data-cal-config='{"layout":"month_view"}'
            >
              Book Your Free Audit
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
